import React, { useState, useEffect, useCallback } from 'react';
import { useDebounce } from '../hooks/useDebounce';
import {
  Box, Typography, Button, Paper, Grid, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, IconButton, Menu, MenuItem, ListItemIcon,
  ListItemText, TextField, alpha, useTheme,
  Skeleton, InputAdornment, Tooltip,
} from '@mui/material';
import {
  Add, Upload, MoreVert, Delete, Visibility, Edit, Block, Search, Receipt,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { useModulePermissions } from '../hooks/useModulePermissions';
import expenseService from '../services/expenseService';
import expenseCategoryService from '../services/expenseCategoryService';
import { Expense, ExpenseStatus, ExpenseCategory } from '../types';
import CreateExpenseDialog from '../components/expenses/CreateExpenseDialog';
import EditExpenseDialog from '../components/expenses/EditExpenseDialog';
import ViewExpenseDialog from '../components/expenses/ViewExpenseDialog';
import BulkUploadDialog from '../components/expenses/BulkUploadDialog';
import ExpenseStatusChip from '../components/expenses/ExpenseStatusChip';
import { EXPENSE_STATUS_FILTER_OPTIONS, expenseStatusColor } from '../components/expenses/expenseStatusFilter';
import ConfirmationDialog from '../components/common/ConfirmationDialog';
import CopyableIdCell from '../components/common/CopyableIdCell';
import TagChip from '../components/common/TagChip';
import SearchableSelect, { SelectOption } from '../components/common/SearchableSelect';
import { tagColor } from '../utils/tagColor';
import { useAuth } from '../context/AuthContext';

const ExpensesPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { currency } = useAuth();
  const { canRead, canWrite } = useModulePermissions();

  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const debouncedSearch = useDebounce(searchFilter, 500);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const loadExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page: page + 1, limit: rowsPerPage };
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.categoryId = categoryFilter;
      if (debouncedSearch) params.search = debouncedSearch;

      const data = await expenseService.getExpenses(params);
      setExpenses(data.expenses);
      setTotalItems(data.pagination.totalItems);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to load expenses', { variant: 'error' });
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [page, rowsPerPage, statusFilter, categoryFilter, debouncedSearch, enqueueSnackbar]);

  const loadCategories = useCallback(async () => {
    try {
      const cats = await expenseCategoryService.getCategories();
      setCategories(cats);
    } catch {
      // non-critical
    }
  }, []);

  useEffect(() => { loadExpenses(); }, [loadExpenses]);
  useEffect(() => { loadCategories(); }, [loadCategories]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, expense: Expense) => {
    setMenuAnchor(event.currentTarget);
    setSelectedExpense(expense);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleCreate = async (formData: FormData) => {
    try {
      await expenseService.createExpense(formData);
      enqueueSnackbar('Expense created successfully', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to create expense', { variant: 'error' });
      throw error;
    }
  };

  const handleEdit = async (id: string, formData: FormData) => {
    try {
      await expenseService.updateExpense(id, formData);
      enqueueSnackbar('Expense updated successfully', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to update expense', { variant: 'error' });
      throw error;
    }
  };

  const handleDelete = async () => {
    if (!selectedExpense) return;
    try {
      await expenseService.deleteExpense(selectedExpense._id);
      enqueueSnackbar('Expense deleted successfully', { variant: 'success' });
      setDeleteOpen(false);
      setSelectedExpense(null);
      loadExpenses();
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to delete expense', { variant: 'error' });
    }
  };

  // Status rendering uses the shared ExpenseStatusChip for visual
  // consistency with the approval page. The reversed state is derived
  // from the expense doc inside the chip.

  const categoryOptions: SelectOption[] = [
    { value: '', label: 'All' },
    ...categories.map(c => ({ value: c._id, label: c.name })),
  ];
  const ColorDot: React.FC<{ color: string }> = ({ color }) => (
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
  );

  if (!canRead('expenses')) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
        <Paper elevation={0} sx={{ p: 5, textAlign: 'center', maxWidth: 420, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
          <Block sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" fontWeight="600" gutterBottom>Access Denied</Typography>
          <Typography variant="body2" color="text.secondary">
            You don't have permission to view Expenses.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (initialLoad && loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={250} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            My Expenses
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Submit and track your expense claims
          </Typography>
        </Box>
        {canWrite('expenses') && (
          <Box display="flex" gap={1}>
            <Button variant="outlined" startIcon={<Upload />} onClick={() => setBulkUploadOpen(true)}>
              Bulk upload
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
              Create Expense
            </Button>
          </Box>
        )}
      </Box>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by title..."
              value={searchFilter}
              onChange={(e) => {
                setSearchFilter(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <SearchableSelect
              label="Status"
              value={statusFilter}
              onChange={(v) => { setStatusFilter(v); setPage(0); }}
              options={EXPENSE_STATUS_FILTER_OPTIONS}
              placeholder="All"
              fullWidth
              minWidth={0}
              renderOptionAdornment={(opt) => opt.value ? <ColorDot color={expenseStatusColor(theme, opt.value)} /> : null}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <SearchableSelect
              label="Category"
              value={categoryFilter}
              onChange={(v) => { setCategoryFilter(v); setPage(0); }}
              options={categoryOptions}
              placeholder="All categories"
              fullWidth
              minWidth={0}
              renderOptionAdornment={(opt) => opt.value ? <ColorDot color={tagColor(opt.label)} /> : null}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {!initialLoad && loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: alpha(theme.palette.background.paper, 0.6),
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        )}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Sub-Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Batch ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: 'center', py: 6 }}>
                    <Receipt sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      {searchFilter || statusFilter || categoryFilter ? 'No expenses match your filters' : 'No expenses yet'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                expenses.map((expense) => (
                  <TableRow key={expense._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {expense.title}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">{currency}</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {expense.amount.toLocaleString()}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(expense.date), 'MMM dd, yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TagChip value={expense.categoryId?.name} />
                    </TableCell>
                    <TableCell>
                      <TagChip value={expense.subCategoryId?.name} />
                    </TableCell>
                    <TableCell>
                      <CopyableIdCell value={expense.batchId} fontWeight={400} truncate />
                    </TableCell>
                    <TableCell>
                      <ExpenseStatusChip expense={expense} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {format(new Date(expense.createdAt), 'MMM dd, yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Actions">
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, expense)}>
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      {/* Context Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem onClick={() => { const e = selectedExpense; handleMenuClose(); setSelectedExpense(e); setViewOpen(true); }}>
          <ListItemIcon><Visibility fontSize="small" /></ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        {selectedExpense?.status === ExpenseStatus.PENDING && canWrite('expenses') && (
          <MenuItem onClick={() => { const e = selectedExpense; handleMenuClose(); setSelectedExpense(e); setEditOpen(true); }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {selectedExpense?.status === ExpenseStatus.PENDING && canWrite('expenses') && (
          <MenuItem onClick={() => { const e = selectedExpense; handleMenuClose(); setSelectedExpense(e); setDeleteOpen(true); }}>
            <ListItemIcon><Delete fontSize="small" /></ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Dialogs */}
      <CreateExpenseDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={() => { setCreateOpen(false); loadExpenses(); }}
        onSubmit={handleCreate}
      />
      <EditExpenseDialog
        open={editOpen} expense={selectedExpense}
        onClose={() => { setEditOpen(false); setSelectedExpense(null); }}
        onSuccess={() => { setEditOpen(false); setSelectedExpense(null); loadExpenses(); }}
        onSubmit={handleEdit}
      />
      <ViewExpenseDialog
        open={viewOpen} expense={selectedExpense}
        onClose={() => { setViewOpen(false); setSelectedExpense(null); }}
      />
      <BulkUploadDialog
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        onSuccess={loadExpenses}
      />
      <ConfirmationDialog
        open={deleteOpen}
        title="Delete Expense"
        message={`Are you sure you want to delete "${selectedExpense?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmColor="error"
        onConfirm={handleDelete}
        onClose={() => { setDeleteOpen(false); setSelectedExpense(null); }}
      />
    </Box>
  );
};

export default ExpensesPage;
